import { useCallback } from "react";

import { mapToAuthError } from "@/providers/auth/auth-errors";
import { getStoredTokens } from "@/providers/auth/token-provider";

const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL || "http://localhost:8103/auth";

/**
 * MFA method types that can be set up
 */
export type MfaMethodType = "SMS_MFA" | "SOFTWARE_TOKEN_MFA" | "EMAIL_OTP";

/**
 * Response from sending MFA setup code
 */
export interface SendMfaCodeResponse {
  success: boolean;
  /** Secret code for TOTP setup (used to generate QR code) */
  secretCode?: string;
  /** Message from the server */
  message?: string;
  /** Error message if not successful */
  error?: string;
}

/**
 * Response from enabling MFA
 */
export interface EnableMfaResponse {
  success: boolean;
  /** Error message if not successful */
  error?: string;
}

/**
 * Response from disabling MFA
 */
export interface DisableMfaResponse {
  success: boolean;
  /** Error message if not successful */
  error?: string;
}

/**
 * MFA status response from GET /auth/mfa/status
 */
export interface MfaStatusResponse {
  user_id: string;
  mfa: {
    status: string;
    phone_number?: string;
    phoneVerified: boolean;
    smsEnabled: boolean;
    totpEnabled: boolean;
    emailEnabled: boolean;
  };
}

/**
 * Generic API response with message
 */
export interface ApiMessageResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Response from phone change
 */
export interface ChangePhoneResponse {
  success: boolean;
  CodeDeliveryDetailsList?: Array<{
    Destination: string;
    DeliveryMedium: string;
    AttributeName: string;
  }>;
  error?: string;
}

/**
 * Return type for useMfaResource hook
 */
export interface UseMfaResourceResult {
  /** Send MFA setup code (SMS, TOTP, or Email) */
  sendCode: (method: MfaMethodType, phoneNumber?: string) => Promise<SendMfaCodeResponse>;
  /** Enable MFA after code verification */
  enableMfa: (
    code: string | undefined,
    method: MfaMethodType,
    preferredMethod?: MfaMethodType | "NONE"
  ) => Promise<EnableMfaResponse>;
  /** Disable MFA method with verification code */
  disableMfa: (method: MfaMethodType, code: string) => Promise<DisableMfaResponse>;
  /** Get current MFA status */
  getMfaStatus: () => Promise<MfaStatusResponse | null>;
  /** Set preferred MFA method */
  setPreferredMethod: (preferredMethod: MfaMethodType) => Promise<ApiMessageResponse>;
  /** Change phone number */
  changePhone: (newPhone: string) => Promise<ChangePhoneResponse>;
  /** Verify phone number with code */
  verifyPhone: (confirmationCode: string) => Promise<ApiMessageResponse>;
  /** Send email verification code (for disabling email MFA) */
  sendEmailVerificationCode: () => Promise<ApiMessageResponse>;
}

/**
 * Helper to make authenticated requests to the auth API
 */
const authFetch = async (path: string, options: RequestInit = {}): Promise<Response> => {
  const tokens = getStoredTokens();
  if (!tokens?.accessToken) {
    throw new Error("Not authenticated. Please login again.");
  }

  return fetch(`${AUTH_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tokens.accessToken}`,
      ...options.headers,
    },
    credentials: "include",
  });
};

/**
 * Hook for MFA setup operations
 *
 * Provides stable function references for MFA API calls.
 */
export const useMfaResource = (): UseMfaResourceResult => {
  /**
   * Send verification code for MFA setup
   * For SMS: sends code to the provided phone number
   * For TOTP: returns the secret code for QR generation
   * For EMAIL_OTP: sends code to user's email (used for disable flow)
   */
  const sendCode = useCallback(async (method: MfaMethodType, phoneNumber?: string): Promise<SendMfaCodeResponse> => {
    try {
      const requestBody: { method: MfaMethodType; methodResource?: { phoneNumber?: string } } = {
        method,
      };
      if (method === "SMS_MFA" && phoneNumber) {
        requestBody.methodResource = { phoneNumber: phoneNumber.replace(/\s/g, "") };
      }

      const response = await authFetch("/mfa/send-code", {
        method: "POST",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const authError = mapToAuthError(errorData);
        return { success: false, error: authError.message };
      }

      const data = await response.json();
      return {
        success: true,
        secretCode: data.secretCode || data.secret || data.code,
        message: data.message,
      };
    } catch (error) {
      const authError = mapToAuthError(error);
      return { success: false, error: authError.message };
    }
  }, []);

  /**
   * Enable MFA after verification code is entered
   * @param code - The verification code (optional for EMAIL_OTP)
   * @param method - The MFA method being enabled
   * @param preferredMethod - The preferred MFA method
   */
  const enableMfa = useCallback(
    async (
      code: string | undefined,
      method: MfaMethodType,
      preferredMethod?: MfaMethodType | "NONE"
    ): Promise<EnableMfaResponse> => {
      try {
        const body: Record<string, unknown> = { method };
        if (code) {
          body.code = code;
        }
        if (preferredMethod) {
          body.preferredMethod = preferredMethod;
        }

        const response = await authFetch("/mfa/enable-mfa", {
          method: "POST",
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const authError = mapToAuthError(errorData);
          return { success: false, error: authError.message };
        }

        return { success: true };
      } catch (error) {
        const authError = mapToAuthError(error);
        return { success: false, error: authError.message };
      }
    },
    []
  );

  /**
   * Disable MFA method after verification code is entered
   */
  const disableMfa = useCallback(async (method: MfaMethodType, code: string): Promise<DisableMfaResponse> => {
    try {
      const response = await authFetch("/mfa/disable-mfa", {
        method: "POST",
        body: JSON.stringify({ method, code }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const authError = mapToAuthError(errorData);
        return { success: false, error: authError.message };
      }

      return { success: true };
    } catch (error) {
      const authError = mapToAuthError(error);
      return { success: false, error: authError.message };
    }
  }, []);

  /**
   * Get current MFA status
   */
  const getMfaStatus = useCallback(async (): Promise<MfaStatusResponse | null> => {
    try {
      const response = await authFetch("/mfa/status", { method: "GET" });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch {
      return null;
    }
  }, []);

  /**
   * Set preferred MFA method
   */
  const setPreferredMethod = useCallback(async (preferredMethod: MfaMethodType): Promise<ApiMessageResponse> => {
    try {
      const response = await authFetch("/mfa/set-preferred", {
        method: "POST",
        body: JSON.stringify({ preferredMethod }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const authError = mapToAuthError(errorData);
        return { success: false, error: authError.message };
      }

      const data = await response.json();
      return { success: true, message: data.message };
    } catch (error) {
      const authError = mapToAuthError(error);
      return { success: false, error: authError.message };
    }
  }, []);

  /**
   * Change phone number (triggers SMS verification)
   */
  const changePhone = useCallback(async (newPhone: string): Promise<ChangePhoneResponse> => {
    try {
      const response = await authFetch("/phone/change", {
        method: "POST",
        body: JSON.stringify({ newPhone: newPhone.replace(/\s/g, "") }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const authError = mapToAuthError(errorData);
        return { success: false, error: authError.message };
      }

      const data = await response.json();
      return { success: true, CodeDeliveryDetailsList: data.CodeDeliveryDetailsList };
    } catch (error) {
      const authError = mapToAuthError(error);
      return { success: false, error: authError.message };
    }
  }, []);

  /**
   * Verify phone number with confirmation code
   */
  const verifyPhone = useCallback(async (confirmationCode: string): Promise<ApiMessageResponse> => {
    try {
      const response = await authFetch("/phone/verify", {
        method: "POST",
        body: JSON.stringify({ confirmationCode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const authError = mapToAuthError(errorData);
        return { success: false, error: authError.message };
      }

      return { success: true };
    } catch (error) {
      const authError = mapToAuthError(error);
      return { success: false, error: authError.message };
    }
  }, []);

  /**
   * Send email verification code (used when disabling Email MFA)
   */
  const sendEmailVerificationCode = useCallback(async (): Promise<ApiMessageResponse> => {
    try {
      const response = await authFetch("/mfa/send-code", {
        method: "POST",
        body: JSON.stringify({ method: "EMAIL_OTP" }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const authError = mapToAuthError(errorData);
        return { success: false, error: authError.message };
      }

      const data = await response.json();
      return { success: true, message: data.message };
    } catch (error) {
      const authError = mapToAuthError(error);
      return { success: false, error: authError.message };
    }
  }, []);

  return {
    sendCode,
    enableMfa,
    disableMfa,
    getMfaStatus,
    setPreferredMethod,
    changePhone,
    verifyPhone,
    sendEmailVerificationCode,
  };
};
